import {
  prepareEvent,
  prepareContractCall,
  readContract,
  type BaseTransactionOptions,
  type AbiParameterToPrimitiveType,
} from "thirdweb";

/**
* Contract events
*/

/**
 * Represents the filters for the "AssetTokenFactoryAddressSet" event.
 */
export type AssetTokenFactoryAddressSetEventFilters = Partial<{
  _previousAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_previousAddress","type":"address"}>
_newAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_newAddress","type":"address"}>
_caller: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_caller","type":"address"}>
}>;

/**
 * Creates an event object for the AssetTokenFactoryAddressSet event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { assetTokenFactoryAddressSetEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  assetTokenFactoryAddressSetEvent({
 *  _previousAddress: ...,
 *  _newAddress: ...,
 *  _caller: ...,
 * })
 * ],
 * });
 * ```
 */
export function assetTokenFactoryAddressSetEvent(filters: AssetTokenFactoryAddressSetEventFilters = {}) {
  return prepareEvent({
    signature: "event AssetTokenFactoryAddressSet(address indexed _previousAddress, address indexed _newAddress, address indexed _caller)",
    filters,
  });
};
  

/**
 * Represents the filters for the "EurPriceFeedAddressSet" event.
 */
export type EurPriceFeedAddressSetEventFilters = Partial<{
  _previousAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_previousAddress","type":"address"}>
_newAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_newAddress","type":"address"}>
_caller: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_caller","type":"address"}>
}>;

/**
 * Creates an event object for the EurPriceFeedAddressSet event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { eurPriceFeedAddressSetEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  eurPriceFeedAddressSetEvent({
 *  _previousAddress: ...,
 *  _newAddress: ...,
 *  _caller: ...,
 * })
 * ],
 * });
 * ```
 */
export function eurPriceFeedAddressSetEvent(filters: EurPriceFeedAddressSetEventFilters = {}) {
  return prepareEvent({
    signature: "event EurPriceFeedAddressSet(address indexed _previousAddress, address indexed _newAddress, address indexed _caller)",
    filters,
  });
};
  

/**
 * Represents the filters for the "PermissionAddressSet" event.
 */
export type PermissionAddressSetEventFilters = Partial<{
  _previousAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_previousAddress","type":"address"}>
_newAddress: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_newAddress","type":"address"}>
_caller: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_caller","type":"address"}>
}>;

/**
 * Creates an event object for the PermissionAddressSet event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { permissionAddressSetEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  permissionAddressSetEvent({
 *  _previousAddress: ...,
 *  _newAddress: ...,
 *  _caller: ...,
 * })
 * ],
 * });
 * ```
 */
export function permissionAddressSetEvent(filters: PermissionAddressSetEventFilters = {}) {
  return prepareEvent({
    signature: "event PermissionAddressSet(address indexed _previousAddress, address indexed _newAddress, address indexed _caller)",
    filters,
  });
};
  

/**
 * Represents the filters for the "RoleAdminChanged" event.
 */
export type RoleAdminChangedEventFilters = Partial<{
  role: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bytes32","name":"role","type":"bytes32"}>
previousAdminRole: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bytes32","name":"previousAdminRole","type":"bytes32"}>
newAdminRole: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bytes32","name":"newAdminRole","type":"bytes32"}>
}>;

/**
 * Creates an event object for the RoleAdminChanged event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { roleAdminChangedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  roleAdminChangedEvent({
 *  role: ...,
 *  previousAdminRole: ...,
 *  newAdminRole: ...,
 * })
 * ],
 * });
 * ```
 */
export function roleAdminChangedEvent(filters: RoleAdminChangedEventFilters = {}) {
  return prepareEvent({
    signature: "event RoleAdminChanged(bytes32 indexed role, bytes32 indexed previousAdminRole, bytes32 indexed newAdminRole)",
    filters,
  });
};
  

/**
 * Represents the filters for the "RoleGranted" event.
 */
export type RoleGrantedEventFilters = Partial<{
  role: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"account","type":"address"}>
sender: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"sender","type":"address"}>
}>;

/**
 * Creates an event object for the RoleGranted event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { roleGrantedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  roleGrantedEvent({
 *  role: ...,
 *  account: ...,
 *  sender: ...,
 * })
 * ],
 * });
 * ```
 */
export function roleGrantedEvent(filters: RoleGrantedEventFilters = {}) {
  return prepareEvent({
    signature: "event RoleGranted(bytes32 indexed role, address indexed account, address indexed sender)",
    filters,
  });
};
  

/**
 * Represents the filters for the "RoleRevoked" event.
 */
export type RoleRevokedEventFilters = Partial<{
  role: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"account","type":"address"}>
sender: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"sender","type":"address"}>
}>;

/**
 * Creates an event object for the RoleRevoked event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { roleRevokedEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  roleRevokedEvent({
 *  role: ...,
 *  account: ...,
 *  sender: ...,
 * })
 * ],
 * });
 * ```
 */
export function roleRevokedEvent(filters: RoleRevokedEventFilters = {}) {
  return prepareEvent({
    signature: "event RoleRevoked(bytes32 indexed role, address indexed account, address indexed sender)",
    filters,
  });
};
  

/**
 * Represents the filters for the "ValidationAsSecurityAndAmountChangedTo" event.
 */
export type ValidationAsSecurityAndAmountChangedToEventFilters = Partial<{
  _caller: AbiParameterToPrimitiveType<{"indexed":true,"internalType":"address","name":"_caller","type":"address"}>
}>;

/**
 * Creates an event object for the ValidationAsSecurityAndAmountChangedTo event.
 * @param filters - Optional filters to apply to the event.
 * @returns The prepared event object.
 * @example
 * ```
 * import { getContractEvents } from "thirdweb";
 * import { validationAsSecurityAndAmountChangedToEvent } from "TODO";
 *
 * const events = await getContractEvents({
 * contract,
 * events: [
 *  validationAsSecurityAndAmountChangedToEvent({
 *  _caller: ...,
 * })
 * ],
 * });
 * ```
 */
export function validationAsSecurityAndAmountChangedToEvent(filters: ValidationAsSecurityAndAmountChangedToEventFilters = {}) {
  return prepareEvent({
    signature: "event ValidationAsSecurityAndAmountChangedTo(bool _value, uint256 _amount, address indexed _caller)",
    filters,
  });
};
  

/**
* Contract read functions
*/



/**
 * Calls the "AUTHORIZED_CALLER_ROLE" function on the contract.
 * @param options - The options for the AUTHORIZED_CALLER_ROLE function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { AUTHORIZED_CALLER_ROLE } from "TODO";
 *
 * const result = await AUTHORIZED_CALLER_ROLE();
 *
 * ```
 */
export async function AUTHORIZED_CALLER_ROLE(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xf573388e",
  [],
  [
    {
      "internalType": "bytes32",
      "name": "",
      "type": "bytes32"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "DEFAULT_ADMIN_ROLE" function on the contract.
 * @param options - The options for the DEFAULT_ADMIN_ROLE function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { DEFAULT_ADMIN_ROLE } from "TODO";
 *
 * const result = await DEFAULT_ADMIN_ROLE();
 *
 * ```
 */
export async function DEFAULT_ADMIN_ROLE(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xa217fddf",
  [],
  [
    {
      "internalType": "bytes32",
      "name": "",
      "type": "bytes32"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "assetTokenFactoryAddress" function on the contract.
 * @param options - The options for the assetTokenFactoryAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { assetTokenFactoryAddress } from "TODO";
 *
 * const result = await assetTokenFactoryAddress();
 *
 * ```
 */
export async function assetTokenFactoryAddress(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xfa2d7504",
  [],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "checkAmount" function.
 */
export type CheckAmountParams = {
  tokenAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_tokenAddress","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_amount","type":"uint256"}>
};

/**
 * Calls the "checkAmount" function on the contract.
 * @param options - The options for the checkAmount function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { checkAmount } from "TODO";
 *
 * const result = await checkAmount({
 *  tokenAddress: ...,
 *  amount: ...,
 * });
 *
 * ```
 */
export async function checkAmount(
  options: BaseTransactionOptions<CheckAmountParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xd6b5983d",
  [
    {
      "internalType": "address",
      "name": "_tokenAddress",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "_amount",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.tokenAddress, options.amount]
  });
};




/**
 * Calls the "eurPriceFeedAddress" function on the contract.
 * @param options - The options for the eurPriceFeedAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { eurPriceFeedAddress } from "TODO";
 *
 * const result = await eurPriceFeedAddress();
 *
 * ```
 */
export async function eurPriceFeedAddress(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x914ac5ac",
  [],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "fiatAmountValidation" function on the contract.
 * @param options - The options for the fiatAmountValidation function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { fiatAmountValidation } from "TODO";
 *
 * const result = await fiatAmountValidation();
 *
 * ```
 */
export async function fiatAmountValidation(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x8043cdbc",
  [],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: []
  });
};


/**
 * Represents the parameters for the "getRoleAdmin" function.
 */
export type GetRoleAdminParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
};

/**
 * Calls the "getRoleAdmin" function on the contract.
 * @param options - The options for the getRoleAdmin function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getRoleAdmin } from "TODO";
 *
 * const result = await getRoleAdmin({
 *  role: ...,
 * });
 *
 * ```
 */
export async function getRoleAdmin(
  options: BaseTransactionOptions<GetRoleAdminParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x248a9ca3",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    }
  ],
  [
    {
      "internalType": "bytes32",
      "name": "",
      "type": "bytes32"
    }
  ]
],
    params: [options.role]
  });
};


/**
 * Represents the parameters for the "getRoleMember" function.
 */
export type GetRoleMemberParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
index: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"index","type":"uint256"}>
};

/**
 * Calls the "getRoleMember" function on the contract.
 * @param options - The options for the getRoleMember function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getRoleMember } from "TODO";
 *
 * const result = await getRoleMember({
 *  role: ...,
 *  index: ...,
 * });
 *
 * ```
 */
export async function getRoleMember(
  options: BaseTransactionOptions<GetRoleMemberParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x9010d07c",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    },
    {
      "internalType": "uint256",
      "name": "index",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: [options.role, options.index]
  });
};


/**
 * Represents the parameters for the "getRoleMemberCount" function.
 */
export type GetRoleMemberCountParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
};

/**
 * Calls the "getRoleMemberCount" function on the contract.
 * @param options - The options for the getRoleMemberCount function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { getRoleMemberCount } from "TODO";
 *
 * const result = await getRoleMemberCount({
 *  role: ...,
 * });
 *
 * ```
 */
export async function getRoleMemberCount(
  options: BaseTransactionOptions<GetRoleMemberCountParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xca15c873",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    }
  ],
  [
    {
      "internalType": "uint256",
      "name": "",
      "type": "uint256"
    }
  ]
],
    params: [options.role]
  });
};


/**
 * Represents the parameters for the "hasRole" function.
 */
export type HasRoleParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"internalType":"address","name":"account","type":"address"}>
};

/**
 * Calls the "hasRole" function on the contract.
 * @param options - The options for the hasRole function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { hasRole } from "TODO";
 *
 * const result = await hasRole({
 *  role: ...,
 *  account: ...,
 * });
 *
 * ```
 */
export async function hasRole(
  options: BaseTransactionOptions<HasRoleParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x91d14854",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    },
    {
      "internalType": "address",
      "name": "account",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.role, options.account]
  });
};


/**
 * Represents the parameters for the "isAccountAuthorized" function.
 */
export type IsAccountAuthorizedParams = {
  account: AbiParameterToPrimitiveType<{"internalType":"address","name":"_account","type":"address"}>
};

/**
 * Calls the "isAccountAuthorized" function on the contract.
 * @param options - The options for the isAccountAuthorized function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { isAccountAuthorized } from "TODO";
 *
 * const result = await isAccountAuthorized({
 *  account: ...,
 * });
 *
 * ```
 */
export async function isAccountAuthorized(
  options: BaseTransactionOptions<IsAccountAuthorizedParams>
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x47539058",
  [
    {
      "internalType": "address",
      "name": "_account",
      "type": "address"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.account]
  });
};




/**
 * Calls the "permissionManagerAddress" function on the contract.
 * @param options - The options for the permissionManagerAddress function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { permissionManagerAddress } from "TODO";
 *
 * const result = await permissionManagerAddress();
 *
 * ```
 */
export async function permissionManagerAddress(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0xbbc8d6e3",
  [],
  [
    {
      "internalType": "address",
      "name": "",
      "type": "address"
    }
  ]
],
    params: []
  });
};




/**
 * Calls the "validateAsSecurityToken" function on the contract.
 * @param options - The options for the validateAsSecurityToken function.
 * @returns The parsed result of the function call.
 * @example
 * ```
 * import { validateAsSecurityToken } from "TODO";
 *
 * const result = await validateAsSecurityToken();
 *
 * ```
 */
export async function validateAsSecurityToken(
  options: BaseTransactionOptions
) {
  return readContract({
    contract: options.contract,
    method: [
  "0x42388851",
  [],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: []
  });
};


/**
* Contract write functions
*/

/**
 * Represents the parameters for the "grantAdminRole" function.
 */
export type GrantAdminRoleParams = {
  account: AbiParameterToPrimitiveType<{"internalType":"address","name":"_account","type":"address"}>
};

/**
 * Calls the "grantAdminRole" function on the contract.
 * @param options - The options for the "grantAdminRole" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { grantAdminRole } from "TODO";
 *
 * const transaction = grantAdminRole({
 *  account: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function grantAdminRole(
  options: BaseTransactionOptions<GrantAdminRoleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xc634b78e",
  [
    {
      "internalType": "address",
      "name": "_account",
      "type": "address"
    }
  ],
  []
],
    params: [options.account]
  });
};


/**
 * Represents the parameters for the "grantRole" function.
 */
export type GrantRoleParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"internalType":"address","name":"account","type":"address"}>
};

/**
 * Calls the "grantRole" function on the contract.
 * @param options - The options for the "grantRole" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { grantRole } from "TODO";
 *
 * const transaction = grantRole({
 *  role: ...,
 *  account: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function grantRole(
  options: BaseTransactionOptions<GrantRoleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x2f2ff15d",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    },
    {
      "internalType": "address",
      "name": "account",
      "type": "address"
    }
  ],
  []
],
    params: [options.role, options.account]
  });
};


/**
 * Represents the parameters for the "initialize" function.
 */
export type InitializeParams = {
  permissionManagerAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_permissionManagerAddress","type":"address"}>
eurPriceFeedAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_eurPriceFeedAddress","type":"address"}>
assetTokenFactoryAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_assetTokenFactoryAddress","type":"address"}>
fiatAmountValidation: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_fiatAmountValidation","type":"uint256"}>
validateAsSecurityToken: AbiParameterToPrimitiveType<{"internalType":"bool","name":"_validateAsSecurityToken","type":"bool"}>
};

/**
 * Calls the "initialize" function on the contract.
 * @param options - The options for the "initialize" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { initialize } from "TODO";
 *
 * const transaction = initialize({
 *  permissionManagerAddress: ...,
 *  eurPriceFeedAddress: ...,
 *  assetTokenFactoryAddress: ...,
 *  fiatAmountValidation: ...,
 *  validateAsSecurityToken: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function initialize(
  options: BaseTransactionOptions<InitializeParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xeea39f79",
  [
    {
      "internalType": "address",
      "name": "_permissionManagerAddress",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "_eurPriceFeedAddress",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "_assetTokenFactoryAddress",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "_fiatAmountValidation",
      "type": "uint256"
    },
    {
      "internalType": "bool",
      "name": "_validateAsSecurityToken",
      "type": "bool"
    }
  ],
  []
],
    params: [options.permissionManagerAddress, options.eurPriceFeedAddress, options.assetTokenFactoryAddress, options.fiatAmountValidation, options.validateAsSecurityToken]
  });
};


/**
 * Represents the parameters for the "isTxAuthorized" function.
 */
export type IsTxAuthorizedParams = {
  tokenAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_tokenAddress","type":"address"}>
from: AbiParameterToPrimitiveType<{"internalType":"address","name":"_from","type":"address"}>
to: AbiParameterToPrimitiveType<{"internalType":"address","name":"_to","type":"address"}>
amount: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_amount","type":"uint256"}>
};

/**
 * Calls the "isTxAuthorized" function on the contract.
 * @param options - The options for the "isTxAuthorized" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { isTxAuthorized } from "TODO";
 *
 * const transaction = isTxAuthorized({
 *  tokenAddress: ...,
 *  from: ...,
 *  to: ...,
 *  amount: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function isTxAuthorized(
  options: BaseTransactionOptions<IsTxAuthorizedParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x3af05a31",
  [
    {
      "internalType": "address",
      "name": "_tokenAddress",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "_from",
      "type": "address"
    },
    {
      "internalType": "address",
      "name": "_to",
      "type": "address"
    },
    {
      "internalType": "uint256",
      "name": "_amount",
      "type": "uint256"
    }
  ],
  [
    {
      "internalType": "bool",
      "name": "",
      "type": "bool"
    }
  ]
],
    params: [options.tokenAddress, options.from, options.to, options.amount]
  });
};


/**
 * Represents the parameters for the "renounceRole" function.
 */
export type RenounceRoleParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"internalType":"address","name":"account","type":"address"}>
};

/**
 * Calls the "renounceRole" function on the contract.
 * @param options - The options for the "renounceRole" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { renounceRole } from "TODO";
 *
 * const transaction = renounceRole({
 *  role: ...,
 *  account: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function renounceRole(
  options: BaseTransactionOptions<RenounceRoleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x36568abe",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    },
    {
      "internalType": "address",
      "name": "account",
      "type": "address"
    }
  ],
  []
],
    params: [options.role, options.account]
  });
};


/**
 * Represents the parameters for the "revokeRole" function.
 */
export type RevokeRoleParams = {
  role: AbiParameterToPrimitiveType<{"internalType":"bytes32","name":"role","type":"bytes32"}>
account: AbiParameterToPrimitiveType<{"internalType":"address","name":"account","type":"address"}>
};

/**
 * Calls the "revokeRole" function on the contract.
 * @param options - The options for the "revokeRole" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { revokeRole } from "TODO";
 *
 * const transaction = revokeRole({
 *  role: ...,
 *  account: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function revokeRole(
  options: BaseTransactionOptions<RevokeRoleParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xd547741f",
  [
    {
      "internalType": "bytes32",
      "name": "role",
      "type": "bytes32"
    },
    {
      "internalType": "address",
      "name": "account",
      "type": "address"
    }
  ],
  []
],
    params: [options.role, options.account]
  });
};


/**
 * Represents the parameters for the "setAssetTokenFactoryAddress" function.
 */
export type SetAssetTokenFactoryAddressParams = {
  assetTokenFactoryAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_assetTokenFactoryAddress","type":"address"}>
};

/**
 * Calls the "setAssetTokenFactoryAddress" function on the contract.
 * @param options - The options for the "setAssetTokenFactoryAddress" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setAssetTokenFactoryAddress } from "TODO";
 *
 * const transaction = setAssetTokenFactoryAddress({
 *  assetTokenFactoryAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setAssetTokenFactoryAddress(
  options: BaseTransactionOptions<SetAssetTokenFactoryAddressParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x358d8222",
  [
    {
      "internalType": "address",
      "name": "_assetTokenFactoryAddress",
      "type": "address"
    }
  ],
  []
],
    params: [options.assetTokenFactoryAddress]
  });
};


/**
 * Represents the parameters for the "setEurPriceFeedAddress" function.
 */
export type SetEurPriceFeedAddressParams = {
  eurPriceFeedAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_eurPriceFeedAddress","type":"address"}>
};

/**
 * Calls the "setEurPriceFeedAddress" function on the contract.
 * @param options - The options for the "setEurPriceFeedAddress" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setEurPriceFeedAddress } from "TODO";
 *
 * const transaction = setEurPriceFeedAddress({
 *  eurPriceFeedAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setEurPriceFeedAddress(
  options: BaseTransactionOptions<SetEurPriceFeedAddressParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x01f6b2e1",
  [
    {
      "internalType": "address",
      "name": "_eurPriceFeedAddress",
      "type": "address"
    }
  ],
  []
],
    params: [options.eurPriceFeedAddress]
  });
};


/**
 * Represents the parameters for the "setPermissionManagerAddress" function.
 */
export type SetPermissionManagerAddressParams = {
  permissionManagerAddress: AbiParameterToPrimitiveType<{"internalType":"address","name":"_permissionManagerAddress","type":"address"}>
};

/**
 * Calls the "setPermissionManagerAddress" function on the contract.
 * @param options - The options for the "setPermissionManagerAddress" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setPermissionManagerAddress } from "TODO";
 *
 * const transaction = setPermissionManagerAddress({
 *  permissionManagerAddress: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setPermissionManagerAddress(
  options: BaseTransactionOptions<SetPermissionManagerAddressParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0xef3e8f22",
  [
    {
      "internalType": "address",
      "name": "_permissionManagerAddress",
      "type": "address"
    }
  ],
  []
],
    params: [options.permissionManagerAddress]
  });
};


/**
 * Represents the parameters for the "setValidationAsSecurityAndAmount" function.
 */
export type SetValidationAsSecurityAndAmountParams = {
  validateAsSecurityToken: AbiParameterToPrimitiveType<{"internalType":"bool","name":"_validateAsSecurityToken","type":"bool"}>
fiatAmountValidation: AbiParameterToPrimitiveType<{"internalType":"uint256","name":"_fiatAmountValidation","type":"uint256"}>
};

/**
 * Calls the "setValidationAsSecurityAndAmount" function on the contract.
 * @param options - The options for the "setValidationAsSecurityAndAmount" function.
 * @returns A prepared transaction object.
 * @example
 * ```
 * import { setValidationAsSecurityAndAmount } from "TODO";
 *
 * const transaction = setValidationAsSecurityAndAmount({
 *  validateAsSecurityToken: ...,
 *  fiatAmountValidation: ...,
 * });
 *
 * // Send the transaction
 * ...
 *
 * ```
 */
export function setValidationAsSecurityAndAmount(
  options: BaseTransactionOptions<SetValidationAsSecurityAndAmountParams>
) {
  return prepareContractCall({
    contract: options.contract,
    method: [
  "0x6cbe31af",
  [
    {
      "internalType": "bool",
      "name": "_validateAsSecurityToken",
      "type": "bool"
    },
    {
      "internalType": "uint256",
      "name": "_fiatAmountValidation",
      "type": "uint256"
    }
  ],
  []
],
    params: [options.validateAsSecurityToken, options.fiatAmountValidation]
  });
};


